import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SubscriptionOrderSuggestion } from './models/suggestion.model';

@Injectable({ providedIn: 'root' })
export class SuggestionsService {
  private readonly baseUrl = '/api/recharge/suggestions';

  /**
   * Fired whenever a suggestion is applied or dismissed. Surfaces (lists,
   * forms, patient records) subscribe so they can refresh themselves without
   * having to coordinate explicit refresh callbacks.
   */
  readonly resolved$ = new Subject<{ id: string; action: 'applied' | 'dismissed' }>();

  constructor(private http: HttpClient) {}

  list(filter: { status?: string; email?: string; shopifyOrderId?: number } = {}): Observable<SubscriptionOrderSuggestion[]> {
    let params = new HttpParams();
    if (filter.status) params = params.set('status', filter.status);
    if (filter.email) params = params.set('email', filter.email);
    if (filter.shopifyOrderId != null) params = params.set('shopifyOrderId', String(filter.shopifyOrderId));
    return this.http.get<SubscriptionOrderSuggestion[]>(this.baseUrl, { params });
  }

  byOrder(shopifyOrderId: number | string): Observable<SubscriptionOrderSuggestion | null> {
    return this.http.get<SubscriptionOrderSuggestion | null>(`${this.baseUrl}/by-order/${shopifyOrderId}`);
  }

  byEmail(email: string): Observable<SubscriptionOrderSuggestion[]> {
    return this.http.get<SubscriptionOrderSuggestion[]>(`${this.baseUrl}/by-email/${encodeURIComponent(email)}`);
  }

  apply(id: string, resolvedBy?: string): Observable<{ success: boolean; suggestion: SubscriptionOrderSuggestion; modifiedOrder?: any }> {
    return this.http
      .post<{ success: boolean; suggestion: SubscriptionOrderSuggestion; modifiedOrder?: any }>(
        `${this.baseUrl}/${id}/apply`,
        { resolvedBy },
      )
      .pipe(tap(() => this.resolved$.next({ id, action: 'applied' })));
  }

  dismiss(id: string, resolvedBy?: string, reason?: string): Observable<{ success: boolean; suggestion: SubscriptionOrderSuggestion }> {
    return this.http
      .post<{ success: boolean; suggestion: SubscriptionOrderSuggestion }>(
        `${this.baseUrl}/${id}/dismiss`,
        { resolvedBy, reason },
      )
      .pipe(tap(() => this.resolved$.next({ id, action: 'dismissed' })));
  }
}
